import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import FileInputExcel from "../Product/FileInputExcel";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import ClientImportTable from "./ClientImportTable";
import ErrorClientList from "./ErrorClientList";
import ExistsClientList from "./ExistsClientList";

type Client = {
    name: string;
    phone: string;
    email?: string;
    exists?: boolean;
    errors?: {
        name?: boolean;
        phone?: boolean;
        email?: boolean;
    } | null;
};

type importStepOneResponse = {
    data: Client[];
    localData: Client[];
    signature: string;
};

interface ClientsImportModalProps {
    toggleImportModal: () => void;
}

export default function ClientsImportModal({ toggleImportModal }: ClientsImportModalProps) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);

    const [file, setFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importingStep, setImportingStep] = useState<1 | 2>(1);
    const [importStepOneResponse, setImportStepOneResponse] = useState<importStepOneResponse | null>(null);
    const clientErrorsArray: string[] = importStepOneResponse?.data
        ? importStepOneResponse.localData.flatMap((client) => {
            if (!client?.errors) return [];

            return Object.entries(client.errors)
                .flatMap(([, value]) => (Array.isArray(value) ? value : []))
                .filter((v): v is string => typeof v === "string");
        })
        : [];


    const clientExistsArray: string[] = importStepOneResponse?.data
        ? importStepOneResponse.localData
            .filter(client => client.exists) // only keep ones with exists = true
            .map(client => client.name || client.phone || "-") // choose what to show
        : [];

    const fileInputRef = useRef<HTMLInputElement>(null);

    const importUrlStepOne = `${baseUrl}/api/dashboard/clients/port/import/parse?locale=${locale}`;
    const importUrlStepTwo = `${baseUrl}/api/dashboard/clients/port/import?locale=${locale}`;

    const handleFileChange = (file: File | null) => {
        setFile(null);
        setImportStepOneResponse(null);
        setImportingStep(1);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setFile(file);
    };

    const onRemoveFile = () => {
        setFile(null);
        setImportStepOneResponse(null);
        setImportingStep(1);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleImportStepOne = async () => {
        if (!file) return;

        const validMimeTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
        ];
        const validExtensions = [".xlsx", ".xls"];
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

        if (!validMimeTypes.includes(file.type) || !validExtensions.includes(ext)) {
            toast.error(t("clientImportModal.invalidFileType"));
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const formData = new FormData();
        formData.append("template", file);

        const loader = toast.loading(t("clientImportModal.loading"));
        setIsImporting(true);

        try {
            const res = await axios.post(importUrlStepOne, formData, {
                headers: {
                    Authorization: token,
                    "Content-Type": "multipart/form-data",
                },
            });

            if (!res.data.success || res.data.error) {
                toast.error(res.data.error || t("clientImportModal.importError"));
                return;
            }

            const parsedData: Client[] = JSON.parse(res.data.data);
            setImportStepOneResponse({
                data: parsedData,
                localData: parsedData,
                signature: res.data.signature,
            });

            setImportingStep(2);
            toast.success(t("clientImportModal.importSuccess"));
        } catch (err) {
            const message = axios.isAxiosError(err)
                ? err.response?.data?.error || t("clientImportModal.importError")
                : t("clientImportModal.unexpectedError");
            toast.error(message);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = "";
            toast.dismiss(loader);
            setIsImporting(false);
        }
    };

    const handleImportStepTwo = async () => {
        if (!file || !importStepOneResponse) return;

        const loader = toast.loading(t("clientImportModal.loading"));
        setIsImporting(true);

        try {
            const body = {
                data: JSON.stringify(importStepOneResponse.data),
                signature: importStepOneResponse.signature,
            };

            const res = await axios.post(importUrlStepTwo, body, {
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
            });

            if (!res.data.success) {
                toast.error(res.data.error || t("clientImportModal.importError"));
                return;
            }

            toast.success(t("clientImportModal.importSuccess"));
            toggleImportModal();
            setFile(null);
            setImportStepOneResponse(null);
        } catch (err) {
            const message = axios.isAxiosError(err)
                ? err.response?.data?.error || t("clientImportModal.importError")
                : t("clientImportModal.unexpectedError");
            toast.error(message);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = "";
            toast.dismiss(loader);
            setIsImporting(false);
        }
    };


    const deleteRow = (index: number) => {
        if (!importStepOneResponse) return;

        const updated = importStepOneResponse.localData.filter((_, i) => i !== index);
        setImportStepOneResponse({
            ...importStepOneResponse,
            localData: updated,
        });
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex w-[95%] flex-col gap-1 rounded-lg bg-white shadow-lg md:w-fit md:min-w-[60%] md:max-w-[90%]">
                <div className="flex items-center justify-between px-2 py-3 sm:px-4 sm:py-4">
                    <h2 className="text-lg font-semibold">{t("clientImportModal.import")}</h2>
                    <button
                        type="button"
                        onClick={toggleImportModal}
                        className="mb-3 text-gray-600 hover:text-gray-800"
                    >
                        <IoMdClose size={24} className="text-red-600" />
                    </button>
                </div>

                <div className="thin-scrollbar flex max-h-[70dvh] flex-col gap-5 overflow-y-auto px-3 pb-3 pt-2 sm:px-5">
                    <div className="flex flex-col gap-5">
                        <div className="flex items-center justify-between gap-3 p-3 pb-1 text-sm">
                            <div className="flex w-full flex-col gap-4">
                                <a
                                    href="https://interflow-production.s3.us-east-1.amazonaws.com/public/templates/clients-template.xlsx"
                                    className="flex w-fit items-center gap-2 rounded-lg bg-skyblue px-4 py-2 text-sm font-semibold text-sidebar transition-colors duration-200 hover:bg-sidebar hover:text-skyblue"
                                >
                                    {t("clientImportModal.downloadTemplate")}
                                </a>

                                <FileInputExcel
                                    onFileChange={handleFileChange}
                                    file={file}
                                    onRemoveFile={onRemoveFile}
                                    disabled={isImporting}
                                />
                                {clientErrorsArray.length > 0 && (
                                    <ErrorClientList errors={clientErrorsArray} />
                                )}
                                {clientExistsArray.length > 0 && (
                                    <ExistsClientList existsList={clientExistsArray} />
                                )}

                                {importStepOneResponse?.localData && (
                                    <ClientImportTable
                                        clients={importStepOneResponse.localData}
                                        deleteRow={deleteRow}
                                    />
                                )}

                                <button
                                    className="mt-3 flex w-fit items-center gap-2 self-end rounded-lg bg-skyblue px-4 py-2 font-semibold text-sidebar transition-colors duration-200 hover:bg-sidebar hover:text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-600"
                                    onClick={
                                        importingStep === 1 ? handleImportStepOne : handleImportStepTwo
                                    }
                                >
                                    {importingStep === 1 ? t("products.check") : t("products.submit")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
